import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  usePartnerGroupChatReactionMutation,
  PARTNER_GROUP_CHAT_REACTION_ACTION,
} from '../../../../api/use-partner-group-chat-reaction-mutation';
import { useAuth } from '../../../../contexts/AuthContext';
import { PARTNER_BRAND } from '../../partnerDashboardTheme';
import { buildPartnerGroupChannelMessageMapOptions } from '../mapPartnerGroupChannel';
import { buildCommunityChatTimeline } from '../communityChatMessageDates';
import { isCurrentUserChatMessage } from '../communityChatReactions';
import CommunityMessageReactions from './CommunityMessageReactions';
import CommunityReactionDetailSheet from './CommunityReactionDetailSheet';
import EditCommunityMessageDialog from './EditCommunityMessageDialog';
import AriaBotAvatar, { isAriaBotMessage, isSupportOutboundMessage } from './AriaBotAvatar';
import CommunityChatAttachments from './CommunityChatAttachments';
import AriaRichMessageContent from '../../../AriaHandsoff/widgets/AriaRichMessageContent';
import AriaInlineRichMessageContent from '../../../AriaHandsoff/widgets/AriaInlineRichMessageContent';
import {
  isAriaInlineRichMessage,
  isAriaRichCardMessage,
} from '../../../AriaHandsoff/ariaMessageTypes';
import { isSharedEventCardMessage } from '../sharedEventMessage';
import SharedEventChatCard from './SharedEventChatCard';
import { formatDate, MOMENT_FORMATS } from '../../../../utils/dateFormat';

const SCROLL_BOTTOM_THRESHOLD = 80;
const CHAT_PANEL_HEIGHT = { xs: 420, sm: 500, md: 580 };

const formatMessageTime = (value) =>
  formatDate(value, MOMENT_FORMATS.TIME) || (value ? String(value) : '');

const ChatDateDivider = ({ label, compact = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', my: compact ? 1 : 1.5 }}>
    <Typography
      variant="caption"
      sx={{
        px: compact ? 1 : 1.25,
        py: 0.375,
        borderRadius: '999px',
        bgcolor: 'action.hover',
        color: 'text.secondary',
        fontWeight: 700,
        fontSize: compact ? '0.625rem' : '0.6875rem',
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ChatMessagesSkeleton = ({ rows = 5 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 0.5 }}>
    {Array.from({ length: rows }).map((_, index) => {
      const alignEnd = index % 2 === 1;
      return (
        <Box
          key={`chat-msg-skel-${index}`}
          sx={{
            display: 'flex',
            flexDirection: alignEnd ? 'row-reverse' : 'row',
            gap: 1.25,
            alignItems: 'flex-start',
          }}
        >
          <Skeleton variant="circular" width={36} height={36} animation="wave" />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: alignEnd ? 'flex-end' : 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mb: 0.75,
                flexDirection: alignEnd ? 'row-reverse' : 'row',
              }}
            >
              <Skeleton variant="rounded" width={88} height={12} animation="wave" />
              <Skeleton variant="rounded" width={40} height={12} animation="wave" />
            </Box>
            <Skeleton
              variant="rounded"
              animation="wave"
              width={`${58 + ((index * 11) % 28)}%`}
              height={52}
              sx={{ borderRadius: alignEnd ? '12px 12px 4px 12px' : '12px 12px 12px 4px' }}
            />
          </Box>
        </Box>
      );
    })}
  </Box>
);

const GetOlderMessagesLabel = ({ loading, onClick }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, pt: 0.5 }}>
    {loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={16} sx={{ color: PARTNER_BRAND }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Loading older messages…
        </Typography>
      </Box>
    ) : (
      <Typography
        component="button"
        type="button"
        onClick={onClick}
        variant="caption"
        sx={{
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          color: PARTNER_BRAND,
          fontWeight: 700,
          fontSize: '0.8125rem',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
          p: 0,
          '&:hover': { color: 'primary.dark' },
          '&:disabled': { color: 'text.disabled', cursor: 'default', textDecoration: 'none' },
        }}
      >
        Get older messages
      </Typography>
    )}
  </Box>
);

const ChatPanel = ({
  communityName,
  communityLogo,
  channelId = null,
  messages = [],
  isLoading = false,
  hasMore = false,
  loadingMore = false,
  readOnly = false,
  sending = false,
  editing = false,
  enableReactions = true,
  enableReactionOpener = true,
  fillHeight = false,
  showLockIcon = true,
  hideHeader = false,
  embedded = false,
  compact = false,
  /** partner = community chat; support = Aria handoff (member left, Aria/admin right) */
  alignMode = 'partner',
  composerPlaceholder = 'Write a message...',
  /** Optional node rendered directly above the send composer (e.g. Aria hand-back bar). */
  composerBanner = null,
  onLoadMore,
  onSendMessage,
  onEditMessage,
  onRefreshMessages,
}) => {
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const pendingScrollHeightRef = useRef(0);
  const hasScrolledToBottomRef = useRef(false);
  const previousMessageCountRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const isLoadPendingRef = useRef(false);
  const [draftMessage, setDraftMessage] = useState('');
  const [reactionPicker, setReactionPicker] = useState(null);
  const [detailMessageId, setDetailMessageId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const reactionMutation = usePartnerGroupChatReactionMutation();
  const { firebaseUid, partnerProfile } = useAuth();
  const messageMapOptions = useMemo(
    () => buildPartnerGroupChannelMessageMapOptions({ firebaseUid, partnerProfile }),
    [firebaseUid, partnerProfile],
  );

  const isNearBottom = useCallback(() => {
    const list = listRef.current;
    if (!list) return true;
    return list.scrollHeight - list.scrollTop - list.clientHeight < SCROLL_BOTTOM_THRESHOLD;
  }, []);

  const handleGetOlderMessages = useCallback(() => {
    const list = listRef.current;
    if (!list || !hasMore || loadingMoreRef.current || isLoadPendingRef.current) {
      return;
    }

    pendingScrollHeightRef.current = list.scrollHeight;
    isLoadPendingRef.current = true;
    onLoadMore?.();
  }, [hasMore, onLoadMore]);

  const handleOpenReactionPicker = useCallback((event, message) => {
    setReactionPicker({ anchorEl: event.currentTarget, messageId: message.id });
  }, []);

  const handleCloseReactionPicker = useCallback(() => {
    setReactionPicker(null);
  }, []);

  const handleSelectReaction = useCallback(
    (message, type) => {
      setReactionPicker(null);
      if (!channelId) {
        return;
      }
      reactionMutation.mutate({
        messageId: message.id,
        type,
        action: PARTNER_GROUP_CHAT_REACTION_ACTION.ADD,
        channelId,
      });
    },
    [channelId, reactionMutation],
  );

  const handleRemoveReaction = useCallback(
    (message, type) => {
      setReactionPicker(null);
      if (!channelId) {
        return;
      }
      reactionMutation.mutate(
        {
          messageId: message.id,
          type,
          action: PARTNER_GROUP_CHAT_REACTION_ACTION.REMOVE,
          channelId,
        },
        {
          onSuccess: async () => {
            setDetailMessageId(null);
            await onRefreshMessages?.();
          },
        },
      );
    },
    [channelId, onRefreshMessages, reactionMutation],
  );

  const handleOpenReactionDetail = useCallback((message) => {
    setDetailMessageId(message.id);
  }, []);

  const handleCloseReactionDetail = useCallback(() => {
    setDetailMessageId(null);
  }, []);

  const canEditMessages = Boolean(onEditMessage) && !readOnly;

  const handleStartEditMessage = useCallback((message) => {
    setEditingMessage(message);
  }, []);

  const handleCancelEditMessage = useCallback(() => {
    setEditingMessage(null);
  }, []);

  const handleSaveEditMessage = useCallback(
    async (messageText) => {
      if (!editingMessage?.id || !messageText.trim() || editing) return;

      try {
        await onEditMessage(editingMessage.id, messageText);
        setEditingMessage(null);
      } catch {
        // Parent mutation handles error toast.
      }
    },
    [editing, editingMessage, onEditMessage],
  );

  const canSendMessage = Boolean(onSendMessage) && !readOnly;
  const trimmedDraft = draftMessage.trim();
  const canSubmitMessage = canSendMessage && Boolean(trimmedDraft) && !sending;

  const handleSendMessage = useCallback(async () => {
    if (!canSubmitMessage) return;

    try {
      await onSendMessage(trimmedDraft);
      setDraftMessage('');
      requestAnimationFrame(() => inputRef.current?.focus());
    } catch {
      // Parent mutation handles error toast.
    }
  }, [canSubmitMessage, onSendMessage, trimmedDraft]);

  const handleMessageKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      event.preventDefault();
      void handleSendMessage();
    },
    [handleSendMessage],
  );

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
    if (!loadingMore) {
      isLoadPendingRef.current = false;
    }
  }, [loadingMore]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    if (pendingScrollHeightRef.current > 0) {
      const heightAdded = list.scrollHeight - pendingScrollHeightRef.current;
      list.scrollTop = heightAdded;
      pendingScrollHeightRef.current = 0;
      return;
    }

    if (!isLoading && messages.length > 0 && !hasScrolledToBottomRef.current) {
      list.scrollTop = list.scrollHeight;
      hasScrolledToBottomRef.current = true;
      previousMessageCountRef.current = messages.length;
      return;
    }

    const messageCountGrew = messages.length > previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;

    if (!isLoading && messageCountGrew && isNearBottom()) {
      list.scrollTop = list.scrollHeight;
    }
  }, [isLoading, isNearBottom, messages]);

  const showGetOlderMessages = hasMore && !isLoading && messages.length > 0;
  const chatTimeline = useMemo(() => buildCommunityChatTimeline(messages), [messages]);
  const detailMessage = messages.find((message) => message.id === detailMessageId) ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: fillHeight ? '100%' : CHAT_PANEL_HEIGHT,
        minHeight: 0,
        border: embedded ? 'none' : '1px solid',
        borderColor: embedded ? undefined : 'divider',
        borderRadius: embedded ? 0 : '12px',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        ...(readOnly
          ? {
              opacity: 0.72,
              filter: 'grayscale(1)',
            }
          : {}),
      }}
    >
      {!hideHeader ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: 'action.hover',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'text.secondary',
            }}
            src={communityLogo}
          >
            {communityName?.charAt(0) || 'C'}
          </Avatar>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: compact ? '0.8125rem' : '0.875rem',
              color: 'text.primary',
            }}
          >
            {communityName}
          </Typography>
          {showLockIcon ? (
            <LockOutlinedIcon
              sx={{ fontSize: compact ? 14 : 16, color: 'text.disabled', ml: 0.25 }}
            />
          ) : null}
        </Box>
      ) : null}

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: compact ? 1.5 : 2,
          py: compact ? 1.5 : 2,
          bgcolor: 'background.default',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {showGetOlderMessages ? (
          <GetOlderMessagesLabel loading={loadingMore} onClick={handleGetOlderMessages} />
        ) : null}

        {isLoading ? <ChatMessagesSkeleton /> : null}

        {!isLoading && messages.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: 'center',
              py: 3,
              fontWeight: 500,
              fontSize: compact ? '0.8125rem' : undefined,
            }}
          >
            No messages yet.
          </Typography>
        ) : null}

        {!isLoading
          ? chatTimeline.map((item) => {
              if (item.kind === 'divider') {
                return <ChatDateDivider key={item.id} label={item.label} compact={compact} />;
              }

              const message = item;
              const isOutboundMessage =
                alignMode === 'support'
                  ? isSupportOutboundMessage(message, { firebaseUid })
                  : isCurrentUserChatMessage(message, messageMapOptions.currentUserIds);
              const canOpenReactionPicker = enableReactions && enableReactionOpener && !readOnly;
              const showReactions =
                enableReactions &&
                (canOpenReactionPicker ||
                  (message.reactionChips?.length ?? 0) > 0 ||
                  (message.latestReactions?.length ?? 0) > 0);
              const isPickerOpenForMessage = reactionPicker?.messageId === message.id;
              const canEditThisMessage = canEditMessages && isOutboundMessage;
              const avatarSize = compact ? 30 : 36;
              const showAriaBotAvatar = isAriaBotMessage(message) && !message.avatarUrl;
              const hasText = Boolean(String(message.text ?? '').trim());
              const hasRichCard = isAriaRichCardMessage(message);
              const hasSharedEventCard = isSharedEventCardMessage(message);
              const hasInlineRichCard = isAriaInlineRichMessage(message);
              const messageAttachments = Array.isArray(message.attachments)
                ? message.attachments
                : [];
              const hasAttachments = messageAttachments.length > 0;

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: isOutboundMessage ? 'row-reverse' : 'row',
                    gap: compact ? 1 : 1.25,
                    mb: compact ? 1.5 : 2,
                  }}
                >
                  {showAriaBotAvatar ? (
                    <AriaBotAvatar size={avatarSize} alt={message.author || 'Aria'} />
                  ) : (
                    <Avatar
                      src={message.avatarUrl}
                      alt={message.author}
                      sx={{ width: avatarSize, height: avatarSize }}
                    />
                  )}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOutboundMessage ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1,
                        mb: 0.5,
                        flexDirection: isOutboundMessage ? 'row-reverse' : 'row',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: compact ? '0.75rem' : undefined,
                        }}
                      >
                        {message.author}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontSize: compact ? '0.625rem' : undefined }}
                      >
                        {formatMessageTime(message.time)}
                      </Typography>
                      {message.editedAt ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.disabled',
                            fontStyle: 'italic',
                            fontSize: compact ? '0.625rem' : undefined,
                          }}
                        >
                          Edited
                        </Typography>
                      ) : null}
                    </Box>
                    {hasText || hasAttachments || hasInlineRichCard ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          maxWidth:
                            hasAttachments || hasInlineRichCard ? (compact ? 280 : 340) : '85%',
                          width: hasAttachments || hasInlineRichCard ? '100%' : 'auto',
                          flexDirection: isOutboundMessage ? 'row-reverse' : 'row',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap:
                              (hasText && (hasAttachments || hasInlineRichCard)) ||
                              (hasAttachments && hasInlineRichCard)
                                ? 0.75
                                : 0,
                            flex: 1,
                            minWidth: 0,
                            maxWidth: '100%',
                          }}
                        >
                          {hasAttachments ? (
                            <Box
                              sx={{
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: compact ? '14px' : '16px',
                                boxShadow: '0 4px 16px rgba(17, 24, 39, 0.06)',
                              }}
                            >
                              <CommunityChatAttachments
                                attachments={messageAttachments}
                                compact={compact}
                                edges="all"
                              />
                            </Box>
                          ) : null}
                          {hasText || hasInlineRichCard ? (
                            <Box
                              sx={{
                                display: 'block',
                                minWidth: 0,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                bgcolor: isOutboundMessage ? 'primary.main' : 'primary.light',
                                border: isOutboundMessage ? 'none' : '1px solid',
                                borderColor: isOutboundMessage ? undefined : 'primary.light',
                                borderRadius: isOutboundMessage
                                  ? '12px 12px 4px 12px'
                                  : '12px 12px 12px 4px',
                                px: compact ? 1.25 : 1.5,
                                py: compact ? 0.75 : 1,
                                alignSelf: isOutboundMessage ? 'flex-end' : 'flex-start',
                              }}
                            >
                              {hasText ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isOutboundMessage
                                      ? 'primary.contrastText'
                                      : 'text.primary',
                                    fontSize: compact ? '0.8125rem' : undefined,
                                    lineHeight: compact ? 1.45 : 1.55,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                    mb: hasInlineRichCard ? (compact ? 0.75 : 1) : 0,
                                  }}
                                >
                                  {message.text}
                                </Typography>
                              ) : null}
                              {hasInlineRichCard ? (
                                <AriaInlineRichMessageContent
                                  message={message}
                                  isOutbound={isOutboundMessage}
                                  compact={compact}
                                />
                              ) : null}
                            </Box>
                          ) : null}
                        </Box>
                        {canEditThisMessage && hasText ? (
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditMessage(message)}
                            aria-label="Edit message"
                            sx={{
                              width: compact ? 24 : 28,
                              height: compact ? 24 : 28,
                              color: 'text.disabled',
                              '&:hover': { color: 'primary.main', bgcolor: 'primary.light' },
                            }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: compact ? 14 : 16 }} />
                          </IconButton>
                        ) : null}
                      </Box>
                    ) : null}
                    {hasRichCard ? (
                      <Box
                        sx={{
                          maxWidth: '100%',
                          alignSelf: isOutboundMessage ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <AriaRichMessageContent message={message} />
                      </Box>
                    ) : null}
                    {hasSharedEventCard ? (
                      <Box
                        sx={{
                          mt: hasText || hasAttachments || hasRichCard || hasInlineRichCard ? 1 : 0,
                          maxWidth: '100%',
                          alignSelf: isOutboundMessage ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <SharedEventChatCard event={message.sharedEventPayload} compact={compact} />
                      </Box>
                    ) : null}
                    {showReactions ? (
                      <Box sx={{ alignSelf: isOutboundMessage ? 'flex-end' : 'flex-start' }}>
                        <CommunityMessageReactions
                          message={message}
                          currentUserReactionContext={messageMapOptions}
                          readOnly={readOnly || !canOpenReactionPicker}
                          enableOpener={canOpenReactionPicker}
                          reactionPickerAnchor={
                            isPickerOpenForMessage ? (reactionPicker?.anchorEl ?? null) : null
                          }
                          onOpenPicker={
                            canOpenReactionPicker ? handleOpenReactionPicker : undefined
                          }
                          onClosePicker={handleCloseReactionPicker}
                          onSelectReaction={
                            canOpenReactionPicker ? handleSelectReaction : undefined
                          }
                          onOpenDetail={handleOpenReactionDetail}
                          isReacting={reactionMutation.isPending}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              );
            })
          : null}
      </Box>

      {canSendMessage ? (
        <>
          {composerBanner}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              px: compact ? 1.5 : 2,
              py: compact ? 1.25 : 1.5,
              borderTop: composerBanner ? 'none' : '1px solid',
              borderTopColor: composerBanner ? undefined : 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <TextField
              inputRef={inputRef}
              value={draftMessage}
              onChange={(event) => setDraftMessage(event.target.value)}
              onKeyDown={handleMessageKeyDown}
              placeholder={composerPlaceholder}
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              margin="none"
              disabled={sending}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: compact ? '10px' : '12px',
                  bgcolor: 'background.default',
                  fontSize: compact ? '0.8125rem' : '0.875rem',
                  minHeight: compact ? 40 : 44,
                  py: 0,
                  alignItems: 'center',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'text.disabled' },
                  '&.Mui-focused fieldset': { borderColor: PARTNER_BRAND },
                },
                '& .MuiOutlinedInput-input': {
                  py: compact ? 1 : 1.125,
                  lineHeight: 1.4,
                },
              }}
            />
            <LoadingButton
              variant="contained"
              loading={sending}
              disabled={!canSubmitMessage}
              onClick={() => void handleSendMessage()}
              aria-label="Send message"
              sx={{
                minWidth: compact ? 40 : 44,
                width: compact ? 40 : 44,
                height: compact ? 40 : 44,
                minHeight: compact ? 40 : 44,
                p: 0,
                borderRadius: compact ? '10px' : '12px',
                flexShrink: 0,
                bgcolor: PARTNER_BRAND,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
                '&.Mui-disabled': {
                  bgcolor: 'action.hover',
                  color: 'text.disabled',
                },
              }}
            >
              {!sending ? <SendRoundedIcon sx={{ fontSize: compact ? 18 : 20 }} /> : null}
            </LoadingButton>
          </Box>
        </>
      ) : null}

      <CommunityReactionDetailSheet
        open={Boolean(detailMessage)}
        message={detailMessage}
        currentUserIds={messageMapOptions.currentUserIds}
        readOnly={readOnly}
        removing={reactionMutation.isPending}
        onClose={handleCloseReactionDetail}
        onRemoveReaction={handleRemoveReaction}
      />

      <EditCommunityMessageDialog
        open={Boolean(editingMessage)}
        initialText={editingMessage?.text ?? ''}
        saving={editing}
        onClose={handleCancelEditMessage}
        onSave={handleSaveEditMessage}
      />
    </Box>
  );
};

export default ChatPanel;
